import { useEffect } from "react";

// Import all icons
import smuIcon from "@/assets/icons/smu.webp";
import umIcon from "@/assets/icons/um.webp";
import awareIcon from "@/assets/icons/aware.webp";
import buergerstiftungIcon from "@/assets/icons/buergerstiftung.webp";
import nurseryIcon from "@/assets/icons/nursery.webp";
import enpalIcon from "@/assets/icons/enpal.webp";
import skipIcon from "@/assets/icons/skip.webp";
import vodafoneIcon from "@/assets/icons/vodafone.webp";
import stclaresIcon from "@/assets/icons/stclares.webp";
import comeniusIcon from "@/assets/icons/comenius.webp";
import figmaIcon from "@/assets/icons/figma.webp";
import framerIcon from "@/assets/icons/framer.webp";
import lovableIcon from "@/assets/icons/lovable.webp";
import notionIcon from "@/assets/icons/notion.webp";
import awareHealthAppIcon from "@/assets/icons/aware-health-app.webp";
import yukaIcon from "@/assets/icons/yuka.webp";
import pushfitIcon from "@/assets/icons/pushfit.webp";
import notebooklmIcon from "@/assets/icons/notebooklm.webp";
import chatgptIcon from "@/assets/icons/chatgpt.webp";
import claudeIcon from "@/assets/icons/claude.webp";
import perplexityIcon from "@/assets/icons/perplexity.webp";
import substackIcon from "@/assets/icons/substack.webp";
import wetransferIcon from "@/assets/icons/wetransfer.webp";
import dazzcamIcon from "@/assets/icons/dazzcam.webp";
import surflineIcon from "@/assets/icons/surfline.webp";
import spielwerkIcon from "@/assets/icons/spielwerk.webp";
import elevenlabsIcon from "@/assets/icons/elevenlabs.webp";
import alltrailsIcon from "@/assets/icons/alltrails.webp";
import flightyIcon from "@/assets/icons/flighty.webp";

// Card backgrounds are loaded on-demand, not preloaded

// Profile images
import profileColor from "@/assets/profile-photo-color.webp";
import profileBw from "@/assets/profile-photo.webp";

// Critical images that must load before first paint (above-the-fold)
const CRITICAL_IMAGES: string[] = [
  profileBw, profileColor, smuIcon, umIcon, awareIcon,
];

// Non-critical images loaded in background after first paint
const DEFERRED_IMAGES: string[] = [
  buergerstiftungIcon, nurseryIcon,
  enpalIcon, skipIcon, vodafoneIcon, stclaresIcon, comeniusIcon,
  figmaIcon, framerIcon, lovableIcon, notionIcon, awareHealthAppIcon,
  yukaIcon, pushfitIcon, notebooklmIcon, chatgptIcon, claudeIcon,
  perplexityIcon, substackIcon, wetransferIcon, dazzcamIcon, surflineIcon,
  spielwerkIcon, elevenlabsIcon, alltrailsIcon, flightyIcon,
  // Card backgrounds are NOT preloaded — they load on demand when detail pages open
];

// Keep image references alive to prevent garbage collection
// This ensures decoded images stay in browser memory
const imageCache: HTMLImageElement[] = [];

let preloaded = false;
let resolveReady: () => void;
let imageReadyFlag = false;

const imageReadyPromise = new Promise<void>((res) => { resolveReady = res; });

function decodeImage(src: string, timeoutMs = 2500): Promise<void> {
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = src;
  document.head.appendChild(link);

  const img = new Image();
  img.src = src;
  imageCache.push(img);

  return new Promise<void>((resolve) => {
    let resolved = false;
    const done = () => {
      if (!resolved) { resolved = true; resolve(); }
    };
    img.decode().then(done).catch(done);
    img.onload = done;
    img.onerror = done;
    setTimeout(done, timeoutMs);
  });
}

function preloadAllImages() {
  if (preloaded) return;
  preloaded = true;

  // Load critical images first, resolve ready once they're done
  Promise.all(CRITICAL_IMAGES.map((src) => decodeImage(src, 1500))).then(() => {
    imageReadyFlag = true;
    resolveReady();
    // Then load deferred images in background
    DEFERRED_IMAGES.forEach((src) => decodeImage(src, 5000));
  });
}

// Preload immediately at module load
preloadAllImages();

export const waitForImages = () => imageReadyPromise;
export const areImagesReady = () => imageReadyFlag;

export function useImagePreloader() {
  useEffect(() => {
    preloadAllImages();
  }, []);
}
